/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import React from "react"
import { ConditionalWrapper, Icon } from "@cd2/components"
import { IconDefinition } from "@fortawesome/pro-solid-svg-icons"
import { useTheme } from "@cd2/theme"
import { ConditionalLink } from "./ConditionalLink"
import { isLight, lightenDarkenColor } from "@cd2/tools"
import { QuickToolTipWrapper } from "./QuickToolTipWrapper"
import { DropdownMenu } from "./DropdownMenu"
import { useConfirmation } from "../hooks/useConfirmation"
import { ConfirmCancelDialogue } from "./ConfirmCancelDialogue"
import { createPortal } from "react-dom"
import { brandColors } from "./EOSThemeProvider"
import { LINK_TOOLTIP_DELAY, LinkTooltipEditButton, useLinkTooltips } from "./LinkTooltips"

export interface IMenuItemProps {
  to?: string
  href?: string
  onClick?: () => void
  disabled?: boolean
  children: React.ReactNode
  icon?: string | IconDefinition
  displayIconSpacing?: boolean
  light?: boolean
  isHeading?: boolean
  exact?: boolean
  className?: string
  linkBadge?: { children: React.ReactNode; color: string; absolutePosition?: boolean }
  badgeCount?: number
  small?: boolean
  submenuAsDropdown?: boolean
  subMenuItems?: IMenuItemProps[]
  activeColor?: string
  buttonColor?: string
  tooltip?: React.ReactNode
  // key of an editable tooltip (see LinkTooltips), shown after a delay when no `tooltip` is set
  tooltipKey?: string
  color?: string
  iconColor?: string
  style?: any
  render?: (props: IMenuItemProps) => React.ReactNode
  customIcon?: React.ReactNode
  rightIcon?: string | IconDefinition
  confirmation?: {
    body: React.ReactNode
    heading: React.ReactNode
  }
}

interface IMenuProps {
  items: (IMenuItemProps | "separator")[]
  light?: boolean
  small?: boolean
  toolbar?: boolean
  className?: string
}

export function Menu({ items, light, small, className, toolbar }: IMenuProps) {
  return (
    <div
      css={css`
        padding: 6px;
        display: ${toolbar ? "flex" : "block"};
        gap: ${toolbar ? "3px" : "0"};
      `}
      className={className}
    >
      {items.map((item, i) => {
        // Returns true if any items contain an icon
        const hasIcon = items.some(item => typeof item === "object" && item.icon !== undefined)

        if (item === "separator") {
          return (
            <div
              key={i}
              css={css`
                margin: 2px 4px;
                border-bottom: 1px solid #ddd;
              `}
            />
          )
        }
        return (
          <div key={i}>
            <ConditionalWrapper
              condition={
                !!(item.subMenuItems && item.subMenuItems.length > 0 && item.submenuAsDropdown)
              }
              Wrapper={DropdownMenu}
              wrapperProps={{
                items: item.subMenuItems!,
              }}
            >
              <MenuItem
                to={item.to}
                href={item.href}
                onClick={item.onClick}
                disabled={item.disabled}
                displayIconSpacing={item.displayIconSpacing ?? hasIcon}
                light={item.light ?? light}
                icon={item.icon}
                isHeading={item.isHeading}
                exact={item.exact}
                linkBadge={item.linkBadge}
                badgeCount={item.badgeCount}
                activeColor={item.activeColor}
                buttonColor={item.buttonColor}
                color={item.color}
                tooltip={item.tooltip}
                tooltipKey={item.tooltipKey}
                iconColor={item.iconColor}
                customIcon={item.customIcon}
                confirmation={item.confirmation}
                style={item.style}
                small={small}
                render={item.render}
                rightIcon={item.rightIcon}
              >
                {item.children}
              </MenuItem>
            </ConditionalWrapper>
            {item.subMenuItems && item.subMenuItems.length > 0 && !item.submenuAsDropdown && (
              <div
                css={css`
                  margin: 0px 0 4px ${item.icon ? 38 : 0}px;
                `}
              >
                <Menu
                  items={item.subMenuItems}
                  light={item.light ?? light}
                  css={css`
                    padding: 0;
                  `}
                  small
                />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export function MenuItem(item: IMenuItemProps) {
  const [confirmNode, getConfirm] = useConfirmation(
    ({ onResolve }: { onResolve(value: boolean): void }) => (
      <ConfirmCancelDialogue
        heading={item.confirmation?.heading}
        body={item.confirmation?.body ?? <React.Fragment />}
        onConfirm={() => onResolve(true)}
        onCancel={() => onResolve(false)}
        isOpen
      />
    ),
  )

  const linkTooltips = useLinkTooltips()
  const customTooltip = item.tooltipKey ? linkTooltips?.tooltips[item.tooltipKey] : undefined
  // hard-coded tooltips (e.g. why a button is disabled) take priority over editable ones
  const showCustomTooltip = !item.tooltip && !!customTooltip

  const hasBadge = item.badgeCount && item.badgeCount > 0

  const theme = useTheme()
  const lightCss = css`
    color: ${item.disabled ? "#999" : item.color ? item.color : "#2b2b2b"};
    text-shadow: 0 1px 0 rgba(100, 100, 100, 0.05);

    &:where(:hover) {
      background-color: ${
        item.disabled
          ? item.buttonColor
            ? item.buttonColor
            : "transparent"
          : item.buttonColor
          ? lightenDarkenColor(item.buttonColor, 0.1)
          : "rgba(0,0,0,0.05)"
      };
    }

    &:where(:active, .active) {
      background-color: ${
        item.disabled
          ? item.activeColor ?? item.buttonColor ?? "transparent"
          : item.activeColor ??
            (item.buttonColor ? lightenDarkenColor(item.buttonColor, 0.1) : "rgba(0,0,0,0.1)")
      }
    }
  ;
  }
  `
  const darkCss = css`
    color: ${item.color ? item.color : hasBadge ? brandColors.mint : "white"};
    ${hasBadge ? "font-weight: 600;" : ""}
    opacity: ${item.disabled ? 0.5 : 1};
    text-shadow: 0 1px 0 rgba(0, 0, 0, 0.1);

    &:where(:hover) {
      background-color: ${item.disabled
        ? item.buttonColor ?? "transparent"
        : item.buttonColor
        ? lightenDarkenColor(item.buttonColor, 0.1)
        : "rgba(255,255,255,0.1)"};
    }

    &:where(:active, .active) {
      background-color: ${item.disabled
        ? item.activeColor ?? item.buttonColor ?? "transparent"
        : item.activeColor ??
          (item.buttonColor ? lightenDarkenColor(item.buttonColor, 0.1) : "rgba(255,255,255,0.2)")};
    }
  `

  const isHeadingCss = css`
    cursor: default;
    opacity: 0.5;
    font-size: 11px;
    font-weight: 400;
    padding: 16px 16px 4px 10px;

    &:first-child {
      padding-top: 4px;
    }

    &:hover {
      background-color: transparent;
    }
  `

  if (item.render) {
    return <div className="menu-item">{item.render(item)}</div>
  }

  async function handleOnClick() {
    if (item.confirmation) {
      const confirm = await getConfirm()
      if (confirm) {
        item.onClick?.()
      }
    } else {
      item.onClick?.()
    }
  }

  return (
    <div
      className="menu-item"
      css={css`
        position: relative;
      `}
    >
      {item.tooltipKey && (
        <LinkTooltipEditButton
          tooltipKey={item.tooltipKey}
          label={typeof item.children === "string" ? item.children : item.tooltipKey}
          hasContent={!!customTooltip}
        />
      )}
      {item.confirmation &&
        document.getElementById("app-scene-portal-ref") &&
        createPortal(confirmNode, document.getElementById("app-scene-portal-ref")!)}
      <QuickToolTipWrapper
        content={
          showCustomTooltip ? (
            <div
              css={css`
                max-width: 300px;
                white-space: pre-wrap;
              `}
            >
              {customTooltip}
            </div>
          ) : (
            item.tooltip
          )
        }
        showToolTip={!!item.tooltip || showCustomTooltip}
        toolTipProps={
          showCustomTooltip
            ? {
                delay: [LINK_TOOLTIP_DELAY, 0],
                placement: item.light ? "right" : "bottom",
                width: "100%",
              }
            : { appendTo: "parent" }
        }
      >
        <ConditionalLink
          to={item.to}
          href={item.href}
          onClick={handleOnClick}
          disabled={item.disabled}
          exact={item.exact}
          css={css`
            text-decoration: none;
            align-items: center;
            padding: ${item.small ? "6px 8px" : "12px 16px"};
            font-size: ${item.small ? 12 : 13}px;
            cursor: ${item.disabled ? "not-allowed" : "pointer"};
            color: ${item.disabled ? "#999" : "#1b1b1b"};
            white-space: nowrap;
            display: flex;
            gap: 8px;
            font-weight: ${item.small ? 400 : 500};
            user-select: none;
            border-radius: ${theme.borderRadius}px;
            width: 100%;
            background-color: ${item.buttonColor ? item.buttonColor : "transparent"};
            transition: background-color 0.2s ease-in-out;

            ${item.light ? darkCss : lightCss}
            ${item.isHeading ? isHeadingCss : ""}
          `}
          className={item.className}
          style={item.style}
        >
          {(item.displayIconSpacing || item.icon) && (
            <div>
              {item.customIcon}
              {item.icon ? (
                <div
                  css={css`
                    min-width: 16px;
                  `}
                >
                  <Icon color={item.iconColor ?? item.color} icon={item.icon} fixedWidth />
                </div>
              ) : (
                <div
                  css={css`
                    width: 16px;
                  `}
                />
              )}
            </div>
          )}
          <div
            css={css`
              flex: 1;
              display: flex;
              gap: 8px;
              position: relative;
            `}
          >
            <span
              css={css`
                flex: 1;
              `}
            >
              {item.children}
            </span>
            {item.linkBadge && (
              <div
                css={css(
                  css`
                    background-color: ${item.linkBadge.color};
                    font-size: ${item.small ? 9 : 11}px;
                    color: ${isLight(item.linkBadge.color) ? "#000" : "#fff"};
                    border-radius: 2px;
                    padding: 2px;
                  `,
                  item.linkBadge.absolutePosition &&
                    css`
                      position: absolute;
                      top: -12px;
                      right: -20px;
                    `,
                )}
              >
                {item.linkBadge.children}
              </div>
            )}
            {!!item.badgeCount && (
              <div
                css={css`
                  position: absolute;
                  right: -10px;
                  top: -2px;
                `}
              >
                <BadgeCount count={item.badgeCount} small={item.small} />
              </div>
            )}
          </div>
          {item.rightIcon && (
            <div
              css={css`
                min-width: 16px;
              `}
            >
              <Icon color={item.iconColor ?? item.color} icon={item.rightIcon} fixedWidth />
            </div>
          )}
        </ConditionalLink>
      </QuickToolTipWrapper>
    </div>
  )
}

export function BadgeCount({ count, small }: { count: number; small?: boolean }) {
  const size = small ? 18 : 20
  const fontSize = small ? 10 : 11
  return (
    <div
      css={css`
        background-color: #ff1e2d;
        height: ${size}px;
        width: ${size}px;
        border-radius: 50%;
        color: white;
        font-size: ${fontSize}px;
        font-weight: 600;
        text-align: center;
        line-height: ${size}px;
      `}
    >
      {count}
    </div>
  )
}
